// src/careComponents/components/VacateBedsDialog.jsx
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Stack,
  TextField,
  MenuItem,
  Checkbox,
  ListItemText,
  Typography,
  CircularProgress,
  Chip,
  Collapse,
} from "@mui/material";
import { useQuery, useMutation } from "@apollo/client";
import { GET_OPEN_BEDS } from "../graphql/queries/care";
import { VACATE_BEDS } from "../graphql/mutations/care";
import useBusinessFromStore from "./hooks/useBusinessFromStore";

function toBool(v) {
  if (v === true) return true;
  if (v === false) return false;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    return ["1", "true", "t", "yes", "y"].includes(s);
  }
  return Boolean(v);
}

export default function VacateBedsDialog({
  open,
  businessId,
  onClose,
  onDone,
}) {
  const { data, loading, error, refetch } = useQuery(GET_OPEN_BEDS, {
    variables: { businessId: Number(businessId) },
    skip: !open || !businessId,
    fetchPolicy: "cache-and-network",
  });

  const [vacateBeds, { loading: saving }] = useMutation(VACATE_BEDS);
  const { reload } = useBusinessFromStore();
  const [selected, setSelected] = React.useState([]);
  const [note, setNote] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setSelected([]);
      setNote("");
    }
  }, [open]);

  // Only OCCUPIED beds
  const occupiedBedOptions = React.useMemo(() => {
    const rooms = data?.getRoomsByBusiness ?? [];
    return rooms.flatMap((r) =>
      (r.beds ?? [])
        .filter((b) => {
          const occ =
            b?.isOccupied ??
            b?.occupied ??
            (b?.isOpen != null ? !b.isOpen : null) ??
            (b?.available != null ? !b.available : null);
          return toBool(occ);
        })
        .map((b) => ({
          id: String(b.id),
          label: `Room ${r.roomNumber ?? r.roomName ?? r.id} — Bed ${
            b.bedLabel ?? b.id
          }`,
        }))
    );
  }, [data]);

  const handleVacate = async () => {
    if (!selected.length || !businessId) return;
    const { data: res } = await vacateBeds({
      variables: {
        businessId: Number(businessId),
        bedIds: selected,
        note: note || null,
      },
    });
    const ok = res?.vacateBeds?.success === true;
    const count = Number(res?.vacateBeds?.count ?? 0);
    if (!ok)
      throw new Error(res?.vacateBeds?.message || "Failed to vacate beds.");
    reload();
    onDone && onDone(count);
    onClose && onClose();
  };

  const noneToVacate = !loading && occupiedBedOptions.length === 0;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      keepMounted
    >
      <DialogTitle>Vacate Beds</DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Stack alignItems="center" py={3}>
            <CircularProgress />
            <Typography mt={2}>Loading beds…</Typography>
          </Stack>
        ) : error ? (
          <Alert severity="error">{String(error?.message || error)}</Alert>
        ) : noneToVacate ? (
          <Alert severity="info">
            All beds are currently available. Nothing to vacate.
          </Alert>
        ) : (
          <Stack spacing={2}>
            {/* 🔻 This disappears immediately when at least one bed is selected */}
            <Collapse in={selected.length === 0} unmountOnExit>
              <Alert severity="warning">
                Select the currently <strong>occupied</strong> beds you want to
                mark as free.
              </Alert>
            </Collapse>

            <TextField
              select
              label="Occupied beds"
              fullWidth
              helperText={
                selected.length ? "" : "Pick one or more beds to vacate"
              }
              SelectProps={{
                multiple: true,
                value: selected,
                onChange: (e) => setSelected(e.target.value),
                renderValue: (sel) => {
                  if (!sel || sel.length === 0) return "None";
                  const set = new Set(sel);
                  const names = occupiedBedOptions
                    .filter((b) => set.has(b.id))
                    .map((b) => b.label);
                  return names.length > 3
                    ? `${names.length} beds selected`
                    : names.join(", ");
                },
                MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
              }}
            >
              {occupiedBedOptions.map((b) => (
                <MenuItem key={b.id} value={b.id} dense>
                  <Checkbox size="small" checked={selected.includes(b.id)} />
                  <ListItemText primary={b.label} />
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Note (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              multiline
              minRows={2}
              fullWidth
            />

            <Typography variant="body2" color="text.secondary">
              Selected: <Chip size="small" label={selected.length} /> bed
              {selected.length === 1 ? "" : "s"}
            </Typography>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleVacate}
          disabled={saving || loading || selected.length === 0 || noneToVacate}
        >
          {saving ? "Vacating…" : "Mark Free"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
